import { zodResolver } from "@hookform/resolvers/zod";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { Stack, Typography } from "@mui/material";
import { useSetHandleMutation } from "Api/Mutations/Social/useSetHandleMutation";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { HandleField } from "Components/Social/HandleField";
import { SocialTermsConsent } from "Components/Social/SocialTermsConsent/SocialTermsConsent";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { useHandleAvailabilityState } from "Hooks/Social/useHandleAvailabilityState";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  getHandleFormSchema,
  type HandleFormModel,
} from "Schemas/handleSchema";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import {
  Resources,
  getResourcePath,
  useResource,
} from "Translations/Resources";
import type { ComponentType, ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

const RESOURCES = Resources.Social.Handle;

type TranslateFn = ReturnType<typeof useResource>["t"];

type HandlePreview = {
  /** "@handle" (or the localized "@…" placeholder while empty) for the search clause. */
  atHandle: string;
  /** Bare slug (or the localized placeholder word) for the profile-URL clause. */
  bareHandle: string;
};

const BENEFITS: Array<{
  key: string;
  Icon: ComponentType;
  renderContent: (t: TranslateFn, preview: HandlePreview) => ReactNode;
}> = [
  {
    key: "search",
    Icon: PersonSearchOutlinedIcon,
    // Live preview: the typed handle is interpolated (bold) into the sentence as they type,
    // both as "@handle" and as the bare slug inside the public profile URL.
    renderContent: (_, preview) => (
      <Trans
        i18nKey={getResourcePath(RESOURCES.StepBenefitSearch)}
        values={{
          handle: preview.atHandle,
          bareHandle: preview.bareHandle,
        }}
        components={{ strong: <strong /> }}
      />
    ),
  },
  {
    key: "follows",
    Icon: PersonAddAlt1OutlinedIcon,
    renderContent: (t) => t(RESOURCES.StepBenefitFollows),
  },
  {
    key: "chat",
    Icon: ChatBubbleOutlineIcon,
    renderContent: (t) => t(RESOURCES.StepBenefitChat),
  },
  {
    key: "leaderboard",
    Icon: EmojiEventsOutlinedIcon,
    renderContent: (t) => t(RESOURCES.StepBenefitLeaderboard),
  },
];

type Props = {
  onNextStepRequested: () => void;
};

/**
 * Skippable onboarding step (ZS-1320): pick a handle for the persona being onboarded.
 * Primary "save" submits the (required) handle and advances; the smaller secondary
 * "I'll add it later" advances without saving — skippers see the dashboard banner nudge
 * and can set the handle on the profile page.
 */
export const HandleStep = ({ onNextStepRequested }: Props) => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const user = useLoggedInUser();
  // A persona that already has a handle sees it prefilled and editable — continuing without
  // edits skips the save, and the skip button disappears (there is nothing to skip).
  const hasHandle = Boolean(user?.handle);
  // First handle = consent to the social-features terms (ZS-1749); a change needs none.
  const isSocialTermsConsentRequired = !hasHandle;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<HandleFormModel>({
    // Default onSubmit mode on purpose — no red validation errors while the user is still
    // typing. Empty/taken input disables the button outright; a shaped-but-invalid value is
    // submittable and the failed attempt shows the full format rules as the field error.
    resolver: zodResolver(
      getHandleFormSchema(t, { isSocialTermsConsentRequired }),
    ),
    defaultValues: { handle: user?.handle ?? "", acceptSocialTerms: false },
  });

  const availability = useHandleAvailabilityState(
    watch("handle"),
    user?.handle,
  );
  const isEmpty = availability.normalizedInput === "";
  const isSocialTermsConsentMissing =
    isSocialTermsConsentRequired && !watch("acceptSocialTerms");

  const handlePreview: HandlePreview = isEmpty
    ? {
        atHandle: t(RESOURCES.StepBenefitSearchHandleFallback),
        bareHandle: t(RESOURCES.StepBenefitSearchUrlFallback),
      }
    : {
        atHandle: `@${availability.normalizedInput}`,
        bareHandle: availability.normalizedInput,
      };

  const getSubmitButtonLabelResource = () => {
    // The first handle is accepted together with the terms, so its button says so.
    if (isSocialTermsConsentRequired) {
      return Resources.Social.Terms.AcceptAndContinue;
    }

    if (availability.isUnchanged) {
      return Resources.Common.Continue;
    }

    return Resources.Common.Save;
  };

  const { mutateAsync: setHandleAsync, isPending } = useSetHandleMutation({
    onError: (error) => {
      // A 409 on Handle means a concurrent taker won between the live availability check
      // and the submit (reserved handles are caught client-side by the schema).
      if (error?.status === 409 && error.data?.errors?.Handle) {
        toast.error(t(RESOURCES.Taken));
        return;
      }

      toast.error(t(RESOURCES.SaveError));
    },
  });

  const onSubmit = async (model: HandleFormModel) => {
    if (!availability.isUnchanged) {
      try {
        await setHandleAsync(model);
      } catch {
        // Feedback already shown by the mutation's onError — stay on the step.
        return;
      }

      dispatch(getUserInfoAsync.request(undefined));
    }

    onNextStepRequested();
  };

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <StepBox
        submitButtonLabel={t(getSubmitButtonLabelResource())}
        submitDisabled={
          (isEmpty && !hasHandle) ||
          availability.isTaken ||
          isSocialTermsConsentMissing
        }
        isSubmitting={isPending}
        submitButtonDescription={
          isEmpty ? (
            <InfoBox
              variant="warning"
              content={t(RESOURCES.StepMissingWarning)}
            />
          ) : undefined
        }
        secondaryButtonLabel={
          hasHandle ? undefined : t(RESOURCES.ContinueWithoutHandle)
        }
        onSecondaryButtonClick={onNextStepRequested}
        secondaryButtonDisabled={isPending}
      >
        <Stack gap={3}>
          <Typography variant="h6" component="h1" fontWeight={500}>
            {t(RESOURCES.StepTitle)}
          </Typography>

          <Typography>{t(RESOURCES.StepDescription)}</Typography>

          <Stack gap={1.5}>
            {BENEFITS.map(({ key, Icon, renderContent }) => (
              <Stack
                key={key}
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={({ palette }) => ({
                  svg: { color: palette.primary.main, fontSize: 20 },
                })}
              >
                <Icon />
                <Typography>{renderContent(t, handlePreview)}</Typography>
              </Stack>
            ))}
          </Stack>

          <HandleField
            control={control}
            errors={errors}
            name="handle"
            availability={availability}
            isChangeNoteVisible={hasHandle}
          />

          {isSocialTermsConsentRequired && (
            <SocialTermsConsent
              control={control}
              errors={errors}
              name="acceptSocialTerms"
            />
          )}
        </Stack>
      </StepBox>
    </FullHeightForm>
  );
};
